import { Link, useLocation } from 'react-router'
import { Button, buttonVariants } from '../components/ui/button'
import { cn } from '../lib/utils'
import { RobotLogo } from '../components/RobotLogo'

interface LoggedOutPageProps {
  audience: 'admin' | 'student'
}

export function LoggedOutPage({ audience }: LoggedOutPageProps) {
  const { search } = useLocation()
  const role = new URLSearchParams(search).get('role')?.toLowerCase()
  const isStudent = audience === 'student' || role === 'student'
  const isTeacher = role === 'teacher'
  const title = isStudent
    ? 'Signed out of Student Chat'
    : isTeacher
      ? 'Signed out of Teacher Workspace'
      : 'Signed out of Admin Workspace'
  const description = isStudent
    ? 'Your student session has ended. Sign in again to open classes and continue learning.'
    : isTeacher
      ? 'Your teacher session has ended. Sign in again to manage classes, people, and classwork.'
      : 'Your admin session has ended. Sign in again to manage the workspace, people, and class setup.'
  const loginHref = isStudent ? '/login' : '/admin/login'
  const loginLabel = isStudent ? 'Go to student sign in' : isTeacher ? 'Go to teacher sign in' : 'Go to admin sign in'

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10 text-foreground">
      <div className="w-full max-w-[520px] rounded-[1.4rem] border-2 border-nb-border bg-secondary-bg p-8 nb-shadow">
        <div className="flex items-center gap-3">
          <RobotLogo className="h-12 w-12" />
          <div className="inline-flex rounded-full border-2 border-nb-border bg-background px-4 py-1 text-sm font-heading text-foreground/75">
            {isStudent ? 'Student session ended' : isTeacher ? 'Teacher session ended' : 'Admin session ended'}
          </div>
        </div>

        <h1 className="mt-5 font-heading text-[2rem] leading-tight">{title}</h1>
        <p className="mt-3 text-base leading-7 text-foreground/65">{description}</p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={loginHref} className={cn(buttonVariants({ variant: 'default' }))}>
            {loginLabel}
          </Link>
        </div>
      </div>
    </div>
  )
}
